<template>
  <div class="private-messages">
    <h2 class="private-messages__title">Private messages</h2>

    <div v-if="dmLoading" class="private-messages__loading">
      <Loader />
    </div>

    <div v-else-if="dmError" class="private-messages__error">
      {{ dmError }}
    </div>

    <div v-else-if="conversations.length === 0" class="private-messages__empty">
      <p>No private messages yet.</p>
    </div>

    <div v-else class="private-messages__panels">
      <div class="private-messages__contacts">
        <button
          v-for="convo in conversations"
          :key="convo.otherUserId"
          type="button"
          class="private-messages__contact"
          :class="{ 'private-messages__contact--active': convo.otherUserId === selectedUserId }"
          @click="selectedUserId = convo.otherUserId"
        >
          <span class="private-messages__contact-name">{{ convo.otherName }}</span>
          <span v-if="isCoach && convo.otherOrganization" class="private-messages__contact-org">
            {{ convo.otherOrganization }}
          </span>
          <span class="private-messages__contact-preview">{{ previewText(convo) }}</span>
          <span class="private-messages__contact-meta">
            <span class="private-messages__contact-date">{{ formatDate(convo.latestCreated) }}</span>
            <span class="private-messages__contact-count">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {{ convo.messages.length }}
            </span>
          </span>
        </button>
      </div>

      <div class="private-messages__thread">
        <template v-if="selectedConversation">
          <ul class="private-messages__msgs">
            <li v-for="msg in selectedConversation.messages" :key="msg.id" class="private-messages__item">
              <DirectMessageBubble :message="msg" :from-me="msg.fromMe" />
            </li>
          </ul>
          <DirectMessageEntry :recipient-id="selectedConversation.otherUserId" />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Loader from '@/components/Loader'
import DirectMessageEntry from '@/components/discussions/DirectMessageEntry'
import DirectMessageBubble from '@/components/discussions/DirectMessageBubble'
import { stringToDatetime } from '@/datetimeUtils'

const store = useStore()

const selectedUserId = ref(null)

const dmMessages = computed(() => store.state.directMessages.messages)
const dmLoading = computed(() => store.state.directMessages.loading)
const dmError = computed(() => store.state.directMessages.error)
const currentUser = computed(() => store.state.user.user)
const isCoach = computed(() => currentUser.value?.type?.type === 'coach')

const conversations = computed(() => {
  const currentUserId = currentUser.value?.id
  const byConversation = new Map()

  for (const msg of dmMessages.value) {
    const authorId = msg.author?.id ?? msg.author_id
    const recipientId = msg.recipient?.id

    const fromMe = authorId === currentUserId
    const otherUserId = fromMe ? recipientId : authorId
    const otherName = fromMe ? msg.recipient?.full_name : msg.author?.full_name
    const otherOrganization = fromMe ? msg.recipient?.organization : msg.author?.organization
    const annotated = { ...msg, fromMe }

    const existing = byConversation.get(otherUserId)
    if (existing) {
      existing.messages.push(annotated)
      if (new Date(msg.created) > new Date(existing.latestCreated)) {
        existing.latestCreated = msg.created
      }
    } else {
      byConversation.set(otherUserId, {
        otherUserId,
        otherName,
        otherOrganization,
        latestCreated: msg.created,
        messages: [annotated],
      })
    }
  }

  for (const convo of byConversation.values()) {
    convo.messages.sort((a, b) => new Date(a.created) - new Date(b.created))
  }

  return Array.from(byConversation.values()).sort(
    (a, b) => new Date(b.latestCreated) - new Date(a.latestCreated)
  )
})

const selectedConversation = computed(() =>
  conversations.value.find((convo) => convo.otherUserId === selectedUserId.value)
)

function previewText(convo) {
  const latest = convo.messages[convo.messages.length - 1]
  if (!latest) return ''
  const content = latest.content || ''
  return content.length > 60 ? content.slice(0, 60) + '…' : content
}

function formatDate(dateStr) {
  return stringToDatetime(dateStr)
}

watch(
  conversations,
  (newVal) => {
    const stillExists = newVal.some((convo) => convo.otherUserId === selectedUserId.value)
    if (!stillExists) {
      selectedUserId.value = newVal.length > 0 ? newVal[0].otherUserId : null
    }
  },
  { immediate: true }
)

function loadDirectMessages() {
  store.dispatch('directMessages/fetchMessages')
}

watch(currentUser, (newVal, oldVal) => {
  if (newVal?.id !== oldVal?.id) {
    loadDirectMessages()
  }
})

onMounted(() => {
  loadDirectMessages()
})
</script>

<style scoped>
.private-messages {
  min-width: 0;
}

.private-messages__title {
  margin: 0 0 1.5rem;
}

.private-messages__loading {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.private-messages__error {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
}

.private-messages__empty {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-grey);
}

.private-messages__empty p {
  margin: 0;
}

.private-messages__panels {
  display: flex;
  flex-direction: column;
}

.private-messages__contacts {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.private-messages__contact {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid var(--gray-200);
  border-radius: 0;
  box-shadow: none;
  padding: 0.6rem 0.5rem;
  margin: 0;
  cursor: pointer;
  height: auto;
  text-align: left;
  gap: 0.15rem;
}

.private-messages__contact:hover {
  background: var(--gray-100);
  box-shadow: none;
}

.private-messages__contact--active {
  background: var(--gray-100);
  border-left: 3px solid var(--halsa-green);
}

.private-messages__contact-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-black);
}

.private-messages__contact-org {
  font-size: 0.75rem;
  color: var(--text-grey);
}

.private-messages__contact-preview {
  font-size: 0.8rem;
  color: var(--text-grey);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.private-messages__contact-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
  margin-top: 0.15rem;
}

.private-messages__contact-date {
  font-size: 0.7rem;
  color: var(--text-grey);
}

.private-messages__contact-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: var(--text-grey);
  flex-shrink: 0;
}

.private-messages__contact-count svg {
  width: 0.95rem;
  height: 0.95rem;
}

.private-messages__thread {
  min-width: 0;
  padding-top: 1rem;
}

.private-messages__thread-title {
  margin: 0 0 0.75rem;
}

@media (min-width: 768px) {
  .private-messages__panels {
    flex-direction: row;
  }

  .private-messages__contacts {
    width: 40%;
    flex-shrink: 0;
  }

  .private-messages__thread {
    border-left: 1px solid var(--gray-200);
    padding-left: 1.5rem;
    padding-top: 0;
    flex: 1;
  }
}

.private-messages__msgs {
  list-style: none;
  margin: 0;
  padding: 0;
}

.private-messages__item {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}
</style>
